import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { motion } from 'framer-motion';
import {
  Users, FolderOpen, AlertTriangle, CheckCircle2, Clock,
  ArrowUpRight, Briefcase, FileCheck, Shield, Eye, Zap
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { UpgradeModal } from '../components/UpgradeModal';
import { useNavigate } from 'react-router-dom';

const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

/* ─── Admin / HR Dashboard ─── */
function AdminDashboard({ stats, navigate, planUsage }) {
  const [showUpgrade, setShowUpgrade] = useState(false);
  const statCards = [
    { label: 'Total Employees', value: stats.total_employees, icon: Users, color: '#4F46E5', bgColor: '#EEF2FF', link: '/employees' },
    { label: 'Active Visas', value: stats.active_visas, icon: CheckCircle2, color: '#10B981', bgColor: '#ECFDF5', link: '/employees?status=active' },
    { label: 'Expiring Soon', value: stats.expiring_soon, icon: Clock, color: '#F59E0B', bgColor: '#FFFBEB', link: '/employees?status=expired' },
    { label: 'Total PAFs', value: stats.total_pafs, icon: FolderOpen, color: '#4F46E5', bgColor: '#EEF2FF', link: '/pafs' },
  ];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {statCards.map((card, i) => (
          <motion.div
            key={card.label}
            {...fadeIn}
            transition={{ duration: 0.3, delay: i * 0.05 }}
            className="bg-white rounded-2xl border border-indigo-100/50 p-6 shadow-sm hover:shadow-md hover:shadow-indigo-100/50 transition-all cursor-pointer group active:scale-[0.98]"
            data-testid={`stat-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => navigate(card.link)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110" style={{ backgroundColor: card.bgColor }}>
                <card.icon className="w-5 h-5" style={{ color: card.color }} strokeWidth={1.5} />
              </div>
              <ArrowUpRight className="w-4 h-4 text-[#94A3B8] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" strokeWidth={1.5} />
            </div>
            <p className="text-3xl font-bold text-[#0F172A] tracking-tight group-hover:text-indigo-600 transition-colors">{card.value}</p>
            <p className="text-sm text-[#64748B] mt-1 group-hover:text-indigo-600 transition-colors">{card.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <motion.div
          {...fadeIn}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="lg:col-span-2 bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm"
          data-testid="compliance-overview"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-[#0F172A] tracking-tight cursor-pointer hover:text-indigo-600 transition-colors" onClick={() => navigate('/pafs')}>Compliance Overview</h2>
            <button onClick={() => navigate('/pafs')} className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline flex items-center gap-1 font-semibold" data-testid="compliance-view-all">
              View PAFs <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
          <div className="flex items-center gap-8 mb-6">
            <div className="relative w-32 h-32 cursor-pointer" onClick={() => navigate('/pafs')} data-testid="compliance-ring">
              <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#EEF2FF" strokeWidth="10" />
                <circle cx="60" cy="60" r="52" fill="none" stroke="#4F46E5" strokeWidth="10"
                  strokeDasharray={`${stats.compliance_rate * 3.267} ${326.7 - stats.compliance_rate * 3.267}`}
                  strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-[#0F172A]">{stats.compliance_rate}%</span>
              </div>
            </div>
            <div className="space-y-3 flex-1">
              {[
                { label: 'Compliant', value: stats.compliant_pafs, color: 'bg-emerald-500' },
                { label: 'Warning', value: stats.warning_pafs, color: 'bg-amber-400' },
                { label: 'Non-Compliant', value: stats.danger_pafs, color: 'bg-red-500' },
              ].map(row => (
                <div key={row.label} className="flex items-center justify-between p-2.5 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => navigate('/pafs')}>
                  <div className="flex items-center gap-2.5">
                    <div className={`w-3 h-3 rounded-full ${row.color}`} />
                    <span className="text-sm text-[#0F172A]">{row.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-[#0F172A]">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
          {stats.total_pafs > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#64748B]">Overall Compliance</span>
                <span className="text-sm font-semibold text-[#0F172A]">{stats.compliance_rate}%</span>
              </div>
              <Progress value={stats.compliance_rate} className="h-2 bg-indigo-50" />
            </div>
          )}
        </motion.div>

        <AlertsPanel alerts={stats.alerts} navigate={navigate} />
      </div>

      {/* Plan Usage */}
      {planUsage && (
        <motion.div
          {...fadeIn}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="mt-5 bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm"
          data-testid="plan-usage-card"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                <Zap className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-base font-bold text-[#0F172A]">{(planUsage.account_type || 'free').charAt(0).toUpperCase() + (planUsage.account_type || 'free').slice(1)} Plan</h2>
                <p className="text-xs text-[#64748B]">Current usage and limits</p>
              </div>
            </div>
            {planUsage.account_type !== 'enterprise' && (
              <button
                onClick={() => setShowUpgrade(true)}
                data-testid="upgrade-plan-btn"
                className="text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg transition-colors"
              >
                Upgrade Plan
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-slate-50/80 border border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#0F172A]">Employees</span>
                <span className="text-xs font-semibold text-[#64748B]">
                  {planUsage.employees_used} / {planUsage.employees_limit >= 999999 ? 'Unlimited' : planUsage.employees_limit}
                </span>
              </div>
              <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    planUsage.employees_limit < 999999 && planUsage.employees_used >= planUsage.employees_limit
                      ? 'bg-red-500'
                      : planUsage.employees_limit < 999999 && planUsage.employees_used >= planUsage.employees_limit * 0.8
                      ? 'bg-amber-500'
                      : 'bg-indigo-500'
                  }`}
                  style={{ width: `${planUsage.employees_limit >= 999999 ? 10 : Math.min((planUsage.employees_used / planUsage.employees_limit) * 100, 100)}%` }}
                />
              </div>
              {planUsage.employees_limit < 999999 && planUsage.employees_used >= planUsage.employees_limit && (
                <p className="text-xs text-red-500 font-medium mt-1.5">Limit reached — upgrade to add more</p>
              )}
            </div>
            <div className="p-4 rounded-xl bg-slate-50/80 border border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#0F172A]">PAF Files</span>
                <span className="text-xs font-semibold text-[#64748B]">
                  {planUsage.pafs_used} / {planUsage.pafs_limit >= 999999 ? 'Unlimited' : planUsage.pafs_limit}
                </span>
              </div>
              <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    planUsage.pafs_limit < 999999 && planUsage.pafs_used >= planUsage.pafs_limit
                      ? 'bg-red-500'
                      : planUsage.pafs_limit < 999999 && planUsage.pafs_used >= planUsage.pafs_limit * 0.8
                      ? 'bg-amber-500'
                      : 'bg-indigo-500'
                  }`}
                  style={{ width: `${planUsage.pafs_limit >= 999999 ? 10 : Math.min((planUsage.pafs_used / planUsage.pafs_limit) * 100, 100)}%` }}
                />
              </div>
              {planUsage.pafs_limit < 999999 && planUsage.pafs_used >= planUsage.pafs_limit && (
                <p className="text-xs text-red-500 font-medium mt-1.5">Limit reached — upgrade to add more</p>
              )}
            </div>
          </div>
        </motion.div>
      )}
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        limitType="employee"
        currentPlan={planUsage?.account_type || 'free'}
        usage={planUsage || {}}
      />
    </>
  );
}

/* ─── Employee Dashboard ─── */
function EmployeeDashboard({ stats, navigate }) {
  const emp = stats.employee;

  if (!emp) {
    return (
      <motion.div {...fadeIn} className="bg-white rounded-2xl border border-indigo-100/50 p-8 shadow-sm text-center" data-testid="employee-no-profile">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-amber-500" strokeWidth={1.5} />
        </div>
        <h2 className="text-xl font-bold text-[#0F172A] mb-2">Profile Not Linked</h2>
        <p className="text-[#64748B] max-w-md mx-auto">Your employee profile hasn't been linked to your account yet. Please contact your administrator to get set up.</p>
      </motion.div>
    );
  }

  const visaStatusColor = {
    active: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    expired: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
    pending: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  }[emp.visa_status] || { bg: 'bg-slate-50', text: 'text-slate-700', dot: 'bg-slate-400' };

  const pafs = stats.pafs || [];
  const compliantCount = pafs.filter(p => p.compliance_status === 'compliant').length;
  const totalDocs = pafs.length;

  return (
    <>
      {/* Profile Card */}
      <motion.div {...fadeIn} transition={{ duration: 0.3 }} className="bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm mb-6" data-testid="employee-profile-card">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white text-xl font-bold shadow-md shadow-indigo-500/20 flex-shrink-0">
            {emp.first_name?.charAt(0)}{emp.last_name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-[#0F172A]">{emp.first_name} {emp.last_name}</h2>
            <p className="text-[#64748B] text-sm">{emp.job_title || 'No title'}{emp.department ? ` - ${emp.department}` : ''}</p>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${visaStatusColor.bg} ${visaStatusColor.text}`}>
            <div className={`w-2 h-2 rounded-full ${visaStatusColor.dot}`} />
            {emp.visa_status ? emp.visa_status.charAt(0).toUpperCase() + emp.visa_status.slice(1) : 'Unknown'}
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {/* Visa Info */}
        <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.05 }}
          className="bg-white rounded-2xl border border-indigo-100/50 p-6 shadow-sm" data-testid="employee-visa-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-bold text-[#0F172A]">Visa Details</h3>
          </div>
          <div className="space-y-3">
            <div><p className="text-xs text-[#94A3B8]">Visa Type</p><p className="text-sm font-semibold text-[#0F172A]">{emp.visa_type || 'N/A'}</p></div>
            <div><p className="text-xs text-[#94A3B8]">LCA Case #</p><p className="text-sm font-semibold text-[#0F172A]">{emp.lca_case_number || 'N/A'}</p></div>
            <div><p className="text-xs text-[#94A3B8]">LCA Expiration</p><p className="text-sm font-semibold text-[#0F172A]">{emp.lca_expiration_date || 'N/A'}</p></div>
          </div>
        </motion.div>

        {/* PAF Status */}
        <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white rounded-2xl border border-indigo-100/50 p-6 shadow-sm cursor-pointer hover:shadow-md transition-all" onClick={() => navigate('/pafs')} data-testid="employee-paf-status">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <FileCheck className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-bold text-[#0F172A]">PAF Compliance</h3>
          </div>
          {totalDocs > 0 ? (
            <div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl font-bold text-[#0F172A]">{compliantCount}</span>
                <span className="text-sm text-[#64748B]">/ {totalDocs} compliant</span>
              </div>
              <Progress value={totalDocs > 0 ? (compliantCount / totalDocs * 100) : 0} className="h-2 bg-emerald-50" />
            </div>
          ) : (
            <p className="text-sm text-[#64748B]">No PAF records found</p>
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.15 }}
          className="bg-white rounded-2xl border border-indigo-100/50 p-6 shadow-sm" data-testid="employee-quick-actions">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-bold text-[#0F172A]">Quick Actions</h3>
          </div>
          <div className="space-y-2">
            <button onClick={() => navigate('/pafs')} className="w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium text-[#0F172A] hover:bg-indigo-50 hover:text-indigo-600 transition-colors flex items-center justify-between" data-testid="employee-view-pafs-btn">
              View My PAFs <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
            <button onClick={() => navigate('/activity')} className="w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium text-[#0F172A] hover:bg-indigo-50 hover:text-indigo-600 transition-colors flex items-center justify-between" data-testid="employee-view-activity-btn">
              Activity Log <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
            <button onClick={() => navigate('/settings')} className="w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium text-[#0F172A] hover:bg-indigo-50 hover:text-indigo-600 transition-colors flex items-center justify-between" data-testid="employee-settings-btn">
              Account Settings <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </div>
        </motion.div>
      </div>

      {/* PAF Documents List */}
      {pafs.length > 0 && (
        <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.2 }} className="bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm mb-6" data-testid="employee-paf-list">
          <h2 className="text-lg font-bold text-[#0F172A] mb-4">Your PAF Documents</h2>
          <div className="space-y-3">
            {pafs.map((paf, i) => {
              const checklist = paf.checklist || {};
              const total = Object.keys(checklist).length;
              const completed = Object.values(checklist).filter(Boolean).length;
              const statusColors = {
                compliant: 'bg-emerald-50 text-emerald-700',
                warning: 'bg-amber-50 text-amber-700',
                non_compliant: 'bg-red-50 text-red-700',
              };
              return (
                <div key={paf.id || i} className="flex items-center justify-between p-4 rounded-xl border border-indigo-50 hover:bg-slate-50 transition-colors" data-testid={`employee-paf-item-${i}`}>
                  <div className="flex items-center gap-3">
                    <FolderOpen className="w-5 h-5 text-indigo-500" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">{paf.visa_type || 'PAF'} - {paf.lca_case_number || 'No case #'}</p>
                      <p className="text-xs text-[#94A3B8]">{total > 0 ? `${completed}/${total} documents complete` : 'No checklist'}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColors[paf.compliance_status] || 'bg-slate-50 text-slate-700'}`}>
                    {(paf.compliance_status || 'unknown').replace('_', '-')}
                  </span>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      <AlertsPanel alerts={stats.alerts} navigate={navigate} />
    </>
  );
}

/* ─── Public Viewer Dashboard ─── */
function PublicDashboard({ stats }) {
  return (
    <div className="max-w-2xl mx-auto">
      <motion.div {...fadeIn} className="bg-white rounded-2xl border border-indigo-100/50 p-8 shadow-sm text-center mb-6" data-testid="public-summary-card">
        <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-5">
          <Eye className="w-7 h-7 text-indigo-600" strokeWidth={1.5} />
        </div>
        <h2 className="text-xl font-bold text-[#0F172A] mb-1">Organization Compliance Summary</h2>
        <p className="text-sm text-[#64748B] mb-6">Read-only overview of the organization's visa compliance status</p>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="p-4 rounded-xl bg-indigo-50/50">
            <p className="text-2xl font-bold text-indigo-600">{stats.total_employees}</p>
            <p className="text-xs text-[#64748B] mt-1">Employees</p>
          </div>
          <div className="p-4 rounded-xl bg-emerald-50/50">
            <p className="text-2xl font-bold text-emerald-600">{stats.compliant_pafs}</p>
            <p className="text-xs text-[#64748B] mt-1">Compliant PAFs</p>
          </div>
          <div className="p-4 rounded-xl bg-indigo-50/50">
            <p className="text-2xl font-bold text-indigo-600">{stats.total_pafs}</p>
            <p className="text-xs text-[#64748B] mt-1">Total PAFs</p>
          </div>
        </div>

        <div className="relative w-36 h-36 mx-auto mb-4" data-testid="public-compliance-ring">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r="52" fill="none" stroke="#EEF2FF" strokeWidth="10" />
            <circle cx="60" cy="60" r="52" fill="none" stroke="#10B981" strokeWidth="10"
              strokeDasharray={`${stats.compliance_rate * 3.267} ${326.7 - stats.compliance_rate * 3.267}`}
              strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-[#0F172A]">{stats.compliance_rate}%</span>
          </div>
        </div>
        <p className="text-sm text-[#64748B]">Overall Compliance Rate</p>
      </motion.div>
    </div>
  );
}

/* ─── Shared Alerts Panel ─── */
function AlertsPanel({ alerts = [], navigate }) {
  return (
    <motion.div
      {...fadeIn}
      transition={{ duration: 0.3, delay: 0.25 }}
      className="bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm"
      data-testid="alerts-panel"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-[#0F172A] tracking-tight">Alerts</h2>
        {alerts.length > 0 && (
          <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">{alerts.length}</span>
        )}
      </div>
      {alerts.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#64748B]">No alerts. Everything looks good!</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {alerts.map((alert, i) => (
            <div
              key={i}
              className={`p-3.5 rounded-xl text-sm cursor-pointer transition-all hover:scale-[1.01] active:scale-[0.99] ${
                alert.type === 'danger' ? 'bg-red-50 text-red-600 hover:bg-red-100/80' : 'bg-amber-50 text-amber-700 hover:bg-amber-100/80'
              }`}
              onClick={() => {
                if (navigate) {
                  if (alert.paf_id) navigate('/pafs');
                  else if (alert.employee_id) navigate('/employees');
                }
              }}
              data-testid={`alert-item-${i}`}
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <span className="flex-1">{alert.message}</span>
                {navigate && <ArrowUpRight className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-60" strokeWidth={2} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

/* ─── Main Dashboard Page ─── */
export default function DashboardPage() {
  const { apiCall, user } = useAuth();
  const { selectedCompanyId } = useCompany();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [planUsage, setPlanUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const role = user?.role || 'admin';

  const greetings = {
    admin: { title: 'Dashboard', subtitle: 'Overview of your H-1B, H-1B1 & E-3 compliance status' },
    hr: { title: 'Dashboard', subtitle: 'Overview of your H-1B, H-1B1 & E-3 compliance status' },
    employee: { title: `Welcome, ${user?.name?.split(' ')[0] || 'there'}`, subtitle: 'Your personal visa compliance overview' },
    public_user: { title: 'Compliance Dashboard', subtitle: 'Read-only view of organization compliance' },
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = selectedCompanyId ? `?company_id=${selectedCompanyId}` : '';
        const [statsRes, usageRes] = await Promise.all([
          apiCall('get', `/dashboard/stats${params}`),
          apiCall('get', '/plan/usage').catch(() => null),
        ]);
        setStats(statsRes.data);
        if (usageRes) setPlanUsage(usageRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [apiCall, selectedCompanyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) return <p className="text-[#64748B]">Failed to load dashboard data.</p>;

  const greeting = greetings[role] || greetings.admin;

  return (
    <div data-testid="dashboard-page">
      <motion.div {...fadeIn} transition={{ duration: 0.3 }}>
        <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight mb-1">{greeting.title}</h1>
        <p className="text-[#64748B] mb-8">{greeting.subtitle}</p>
      </motion.div>

      {(role === 'admin' || role === 'hr') && <AdminDashboard stats={stats} navigate={navigate} planUsage={planUsage} />}
      {role === 'employee' && <EmployeeDashboard stats={stats} navigate={navigate} />}
      {role === 'public_user' && <PublicDashboard stats={stats} />}
    </div>
  );
}
